import { Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { IPostingPlanViewEntity } from '~/domain/PermanentJobs';
import { formatListingFee } from '~/components/PermanentJobsV2/utils';
import { PromoCodeSection } from './PromoCodeSection';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { EnforcePromoCode } from '~/constants/ff.posthog';

interface IPaymentDetailsPreviewProps {
  plan?: IPostingPlanViewEntity;
  discountedPrice?: number;
  onPromoApply?: (code: string, finalPrice: number) => void;
  onPromoRemove?: () => void;
}

export const PaymentDetailsPreview = ({
  plan,
  discountedPrice,
  onPromoApply,
  onPromoRemove,
}: IPaymentDetailsPreviewProps) => {
  const isPromoCodeEnabled = useFeatureFlag(EnforcePromoCode);
  const originalPrice = plan?.finalPrice ? formatListingFee(plan.finalPrice) : '-';
  const price = discountedPrice !== undefined ? formatListingFee(discountedPrice) : originalPrice;

  return (
    <Column gap={4}>
      <Typography as="span" size="heading_4" bold>
        Payment
      </Typography>

      <Column pt={6} pb={8} px={16} className="border border-neutral-100 rounded-lg">
        <Column gap={5}>
          <Column py={3} px={4} className="bg-neutral-50 rounded">
            <Typography as="span" size="body_small">
              You&apos;ll be charged when your job is posted, and your listing will renew monthly
              unless you choose to cancel.
            </Typography>
          </Column>

          <Column pt={6} pb={8} px={6} className="shadow-elevation-4 rounded-field">
            <Typography as="span" variant="body1" bold color={colors.secondary[500]}>
              Job Listing Fee
            </Typography>
            <Typography as="span" size="caption" color={colors.neutral[500]}>
              {discountedPrice !== undefined && originalPrice !== '-' && (
                <Typography
                  as="span"
                  size="heading_2"
                  bold
                  color={colors.neutral[600]}
                  style={{ textDecoration: 'line-through', marginRight: 4 }}
                >
                  ${originalPrice}
                </Typography>
              )}
              <Typography as="span" size="heading_2" bold color={colors.neutral[600]}>
                ${price}
              </Typography>{' '}
              /monthly
            </Typography>
            <Typography variant="body1" italic color={colors.neutral[500]} style={{ marginTop: 10 }}>
              Your job will be active and visible to candidates.
            </Typography>
          </Column>
        </Column>
      </Column>

      {isPromoCodeEnabled && plan && onPromoApply && (
        <Column pt={4}>
          <PromoCodeSection
            basePrice={plan.basePrice}
            onApply={onPromoApply}
            onRemove={onPromoRemove}
          />
        </Column>
      )}
    </Column>
  );
};
